/**
 * The page's side of the service worker: say when a new version is waiting, say
 * when the network goes away and comes back, and offer "install" where the
 * browser does.
 *
 * ◆ Nothing here reloads the page on its own. A reader in the middle of entering
 * a birth is asked, and the reload is theirs. The banner is a status line, not a
 * modal, and it is dismissible.
 *
 * ⚠ Text comes from the `ui.*` bundle. Until that bundle is loaded `t()` returns
 * the key, so `say()` falls back to English rather than showing `ui.offline`.
 *
 * © Manish Jagdish Thatte
 */

import { t } from './i18n.js';
import { h } from './dom.js';

const FALLBACK = Object.freeze({
  offline: 'You are offline. Pages you have opened before, and the chart tools, still work.',
  online: 'Back online.',
  updateReady: 'A new version is ready. Reload to use it.',
  reload: 'Reload',
  install: 'Install as an app'
});

function say(key) {
  const got = t(`ui.${key}`);
  return got && got !== `ui.${key}` ? got : FALLBACK[key];
}

let region = null;

/** The one place banners are drawn: a polite live region at the top of the page. */
function bannerRegion() {
  if (region && region.isConnected) return region;
  region = h('div', { class: 'app-banners', role: 'status', 'aria-live': 'polite' });
  document.body.prepend(region);
  return region;
}

/** Show a banner; returns a function that removes it. `action` is `{ label, run }`. */
export function banner(id, text, action = null, { autoHideMs = 0 } = {}) {
  const host = bannerRegion();
  host.querySelector(`[data-banner="${id}"]`)?.remove();
  const close = h('button', { type: 'button', class: 'button button--quiet', 'aria-label': 'Dismiss' }, '×');
  const node = h('p', { class: 'app-banner', dataset: { banner: id } },
    h('span', {}, text),
    action ? h('button', { type: 'button', class: 'button', onclick: action.run }, action.label) : null,
    close);
  const remove = () => node.remove();
  close.addEventListener('click', remove);
  host.append(node);
  if (autoHideMs) setTimeout(remove, autoHideMs);
  return remove;
}

/** Offer the reload when a worker is waiting; reload once it has taken over. */
export function watchUpdates(registration) {
  if (!registration) return;
  const offer = (worker) => banner('update', say('updateReady'), {
    label: say('reload'),
    run: () => worker.postMessage({ type: 'SKIP_WAITING' })
  });
  if (registration.waiting && navigator.serviceWorker.controller) offer(registration.waiting);
  registration.addEventListener('updatefound', () => {
    const worker = registration.installing;
    worker?.addEventListener('statechange', () => {
      // "installed" with an existing controller means an update, not the first visit.
      if (worker.state === 'installed' && navigator.serviceWorker.controller) offer(worker);
    });
  });
  // ⚠ On the very first visit the new worker claims the page (clients.claim), which also
  // fires `controllerchange`. Reloading then would throw away what the reader has just typed.
  const hadController = !!navigator.serviceWorker.controller;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !hadController) return;
    reloading = true;
    location.reload();
  });
}

/** Say so when the connection drops or returns. */
export function watchNetwork() {
  const off = () => banner('network', say('offline'));
  window.addEventListener('offline', off);
  window.addEventListener('online', () => {
    document.querySelector('[data-banner="network"]')?.remove();
    banner('online', say('online'), null, { autoHideMs: 4000 });
  });
  if (navigator.onLine === false) off();
}

/** Offer install where the browser supports it. The event is kept until the reader asks. */
export function watchInstall() {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    banner('install', say('install'), {
      label: say('install'),
      run: async () => {
        document.querySelector('[data-banner="install"]')?.remove();
        await event.prompt();
      }
    });
  });
}

/** Everything the page does about the worker, in one call from main.js. */
export function startPwa(registration) {
  watchNetwork();
  watchInstall();
  watchUpdates(registration);
}
